/**
 * Jertscript Abstract Syntax Tree (AST) Node Types
 *
 * This module defines all the node types that make up the Jertscript AST.
 * The parser transforms tokens into this tree structure, which the generator
 * then transforms into JavaScript code.
 */

import { Token } from '../lexer/tokens';

// ============================================================================
// Base Interfaces
// ============================================================================

/**
 * Base interface for all AST nodes
 */
export interface ASTNode {
  type: string;
  line: number;
}

/**
 * Base interface for all expression nodes
 */
export interface Expression extends ASTNode {
  type: string;
}

/**
 * Base interface for all statement nodes
 */
export interface Statement extends ASTNode {
  type: string;
}

// ============================================================================
// Expression Nodes
// ============================================================================

/**
 * Binary expression: a op b
 * Examples: 1 + 2, x et y, a est b
 */
export interface BinaryExpr extends Expression {
  type: 'BinaryExpr';
  left: Expression;
  operator: Token;
  right: Expression;
}

/**
 * Unary expression: op a
 * Examples: !x, -5, pas oui
 */
export interface UnaryExpr extends Expression {
  type: 'UnaryExpr';
  operator: Token;
  operand: Expression;
}

/**
 * Literal expression: numbers, strings, booleans, null
 * Examples: 42, "bonjour", oui, non, rien
 */
export interface LiteralExpr extends Expression {
  type: 'LiteralExpr';
  value: string | number | boolean | null;
}

/**
 * Identifier expression: variable names
 * Examples: x, monNom, laValeur
 */
export interface IdentifierExpr extends Expression {
  type: 'IdentifierExpr';
  name: string;
}

/**
 * Call expression: function calls
 * Examples: dire("Bonjour"), calculer(1, 2)
 */
export interface CallExpr extends Expression {
  type: 'CallExpr';
  callee: Expression;
  args: Expression[];
  paren: Token; // The closing paren, for error reporting
}

/**
 * Assignment expression: x = value
 */
export interface AssignmentExpr extends Expression {
  type: 'AssignmentExpr';
  name: Token;
  value: Expression;
}

/**
 * Member assignment expression: obj.prop = value or obj[key] = value
 */
export interface MemberAssignmentExpr extends Expression {
  type: 'MemberAssignmentExpr';
  object: Expression;
  property: Expression;
  computed: boolean;  // true for obj[key], false for obj.prop
  value: Expression;
}

/**
 * Logical expression: a et b, a ou b
 * Separated from BinaryExpr for short-circuit evaluation
 */
export interface LogicalExpr extends Expression {
  type: 'LogicalExpr';
  left: Expression;
  operator: Token;
  right: Expression;
}

/**
 * Grouping expression: (expr)
 * Used for explicit precedence
 */
export interface GroupingExpr extends Expression {
  type: 'GroupingExpr';
  expression: Expression;
}

/**
 * Member access expression: obj.property or obj[expr]
 */
export interface MemberExpr extends Expression {
  type: 'MemberExpr';
  object: Expression;
  property: Expression;
  computed: boolean; // true for obj[expr], false for obj.property
}

/**
 * Array literal expression: [1, 2, 3]
 */
export interface ArrayExpr extends Expression {
  type: 'ArrayExpr';
  elements: Expression[];
}

/**
 * Object literal expression: { key: value }
 */
export interface ObjectExpr extends Expression {
  type: 'ObjectExpr';
  properties: ObjectProperty[];
}

export interface ObjectProperty {
  key: Token;
  value: Expression;
  computed: boolean;
}

/**
 * 'ceci' (this) expression
 */
export interface ThisExpr extends Expression {
  type: 'ThisExpr';
  keyword: Token;
}

/**
 * 'nouveau' (new) expression: nouveau Classe()
 */
export interface NewExpr extends Expression {
  type: 'NewExpr';
  callee: Expression;
  args: Expression[];
}

/**
 * Await expression: attendre promesse
 */
export interface AwaitExpr extends Expression {
  type: 'AwaitExpr';
  argument: Expression;
}

// ============================================================================
// Statement Nodes
// ============================================================================

/**
 * Expression statement: expression followed by semicolon
 */
export interface ExpressionStmt extends Statement {
  type: 'ExpressionStmt';
  expression: Expression;
}

/**
 * Variable declaration: croissant x = 42 or baguette y = "hello"
 */
export interface VariableDeclaration extends Statement {
  type: 'VariableDeclaration';
  kind: 'croissant' | 'baguette'; // const or let
  name: Token;
  initializer: Expression | null;
}

/**
 * Function declaration: fonction nom(params) { body }
 */
export interface FunctionDeclaration extends Statement {
  type: 'FunctionDeclaration';
  name: Token;
  params: Token[];
  body: Statement[];
  isAsync: boolean; // asynchrone fonction
}

/**
 * If statement: franchement (condition) { then } sinon { else }
 */
export interface IfStatement extends Statement {
  type: 'IfStatement';
  condition: Expression;
  thenBranch: Statement;
  elseBranch: Statement | null;
}

/**
 * While statement: pendant (condition) { body }
 */
export interface WhileStatement extends Statement {
  type: 'WhileStatement';
  condition: Expression;
  body: Statement;
}

/**
 * For statement: pour (init; condition; increment) { body }
 */
export interface ForStatement extends Statement {
  type: 'ForStatement';
  initializer: Statement | null;
  condition: Expression | null;
  increment: Expression | null;
  body: Statement;
}

/**
 * Return statement: retourner value
 */
export interface ReturnStatement extends Statement {
  type: 'ReturnStatement';
  keyword: Token;
  value: Expression | null;
}

/**
 * Block statement: { statements }
 */
export interface BlockStatement extends Statement {
  type: 'BlockStatement';
  statements: Statement[];
}

/**
 * Try-catch-finally statement:
 * malheureusement { try } merde (e) { catch } quandmeme { finally }
 */
export interface TryCatchStatement extends Statement {
  type: 'TryCatchStatement';
  tryBlock: BlockStatement;
  catchParam: Token | null;
  catchBlock: BlockStatement | null;
  finallyBlock: BlockStatement | null;
}

/**
 * Class declaration: classe Nom { methods }
 */
export interface ClassDeclaration extends Statement {
  type: 'ClassDeclaration';
  name: Token;
  methods: FunctionDeclaration[];
  superclass: IdentifierExpr | null;
}

/**
 * Import declaration: importer { items } de "module"
 */
export interface ImportDeclaration extends Statement {
  type: 'ImportDeclaration';
  specifiers: ImportSpecifier[];
  source: Token; // The string literal with module path
}

export interface ImportSpecifier {
  imported: Token;
  local: Token;
}

/**
 * Export declaration: exporter { items } or exporter fonction/classe
 */
export interface ExportDeclaration extends Statement {
  type: 'ExportDeclaration';
  declaration: Statement | null; // for "exporter fonction foo() {}"
  specifiers: ExportSpecifier[]; // for "exporter { foo, bar }"
}

export interface ExportSpecifier {
  local: Token;
  exported: Token;
}

/**
 * Break statement (for loops)
 */
export interface BreakStatement extends Statement {
  type: 'BreakStatement';
  keyword: Token;
}

/**
 * Continue statement (for loops)
 */
export interface ContinueStatement extends Statement {
  type: 'ContinueStatement';
  keyword: Token;
}

/**
 * Throw statement (for error handling)
 */
export interface ThrowStatement extends Statement {
  type: 'ThrowStatement';
  keyword: Token;
  argument: Expression;
}

// ============================================================================
// Program Node
// ============================================================================

/**
 * The root node representing a complete Jertscript program
 */
export interface Program extends ASTNode {
  type: 'Program';
  statements: Statement[];
}

// ============================================================================
// Union Types for Type Guards
// ============================================================================

export type ExpressionNode =
  | BinaryExpr
  | UnaryExpr
  | LiteralExpr
  | IdentifierExpr
  | CallExpr
  | AssignmentExpr
  | MemberAssignmentExpr
  | LogicalExpr
  | GroupingExpr
  | MemberExpr
  | ArrayExpr
  | ObjectExpr
  | ThisExpr
  | NewExpr
  | AwaitExpr;

export type StatementNode =
  | ExpressionStmt
  | VariableDeclaration
  | FunctionDeclaration
  | IfStatement
  | WhileStatement
  | ForStatement
  | ReturnStatement
  | BlockStatement
  | TryCatchStatement
  | ClassDeclaration
  | ImportDeclaration
  | ExportDeclaration
  | BreakStatement
  | ContinueStatement
  | ThrowStatement;

// ============================================================================
// Factory Functions
// ============================================================================

export function createBinaryExpr(left: Expression, operator: Token, right: Expression): BinaryExpr {
  return { type: 'BinaryExpr', left, operator, right, line: operator.line };
}

export function createUnaryExpr(operator: Token, operand: Expression): UnaryExpr {
  return { type: 'UnaryExpr', operator, operand, line: operator.line };
}

export function createLiteralExpr(value: string | number | boolean | null, line: number): LiteralExpr {
  return { type: 'LiteralExpr', value, line };
}

export function createIdentifierExpr(name: string, line: number): IdentifierExpr {
  return { type: 'IdentifierExpr', name, line };
}

export function createCallExpr(callee: Expression, args: Expression[], paren: Token): CallExpr {
  return { type: 'CallExpr', callee, args, paren, line: paren.line };
}

export function createAssignmentExpr(name: Token, value: Expression): AssignmentExpr {
  return { type: 'AssignmentExpr', name, value, line: name.line };
}

export function createMemberAssignmentExpr(
  object: Expression,
  property: Expression,
  computed: boolean,
  value: Expression,
  line: number
): MemberAssignmentExpr {
  return { type: 'MemberAssignmentExpr', object, property, computed, value, line };
}

export function createLogicalExpr(left: Expression, operator: Token, right: Expression): LogicalExpr {
  return { type: 'LogicalExpr', left, operator, right, line: operator.line };
}

export function createGroupingExpr(expression: Expression, line: number): GroupingExpr {
  return { type: 'GroupingExpr', expression, line };
}

export function createMemberExpr(object: Expression, property: Expression, computed: boolean, line: number): MemberExpr {
  return { type: 'MemberExpr', object, property, computed, line };
}

export function createArrayExpr(elements: Expression[], line: number): ArrayExpr {
  return { type: 'ArrayExpr', elements, line };
}

export function createObjectExpr(properties: ObjectProperty[], line: number): ObjectExpr {
  return { type: 'ObjectExpr', properties, line };
}

export function createThisExpr(keyword: Token): ThisExpr {
  return { type: 'ThisExpr', keyword, line: keyword.line };
}

export function createNewExpr(callee: Expression, args: Expression[], line: number): NewExpr {
  return { type: 'NewExpr', callee, args, line };
}

export function createAwaitExpr(argument: Expression, line: number): AwaitExpr {
  return { type: 'AwaitExpr', argument, line };
}

export function createExpressionStmt(expression: Expression): ExpressionStmt {
  return { type: 'ExpressionStmt', expression, line: expression.line };
}

export function createVariableDeclaration(
  kind: 'croissant' | 'baguette',
  name: Token,
  initializer: Expression | null
): VariableDeclaration {
  return { type: 'VariableDeclaration', kind, name, initializer, line: name.line };
}

export function createFunctionDeclaration(
  name: Token,
  params: Token[],
  body: Statement[],
  isAsync: boolean = false
): FunctionDeclaration {
  return { type: 'FunctionDeclaration', name, params, body, isAsync, line: name.line };
}

export function createIfStatement(
  condition: Expression,
  thenBranch: Statement,
  elseBranch: Statement | null,
  line: number
): IfStatement {
  return { type: 'IfStatement', condition, thenBranch, elseBranch, line };
}

export function createWhileStatement(condition: Expression, body: Statement, line: number): WhileStatement {
  return { type: 'WhileStatement', condition, body, line };
}

export function createForStatement(
  initializer: Statement | null,
  condition: Expression | null,
  increment: Expression | null,
  body: Statement,
  line: number
): ForStatement {
  return { type: 'ForStatement', initializer, condition, increment, body, line };
}

export function createReturnStatement(keyword: Token, value: Expression | null): ReturnStatement {
  return { type: 'ReturnStatement', keyword, value, line: keyword.line };
}

export function createBlockStatement(statements: Statement[], line: number): BlockStatement {
  return { type: 'BlockStatement', statements, line };
}

export function createTryCatchStatement(
  tryBlock: BlockStatement,
  catchParam: Token | null,
  catchBlock: BlockStatement | null,
  finallyBlock: BlockStatement | null,
  line: number
): TryCatchStatement {
  return { type: 'TryCatchStatement', tryBlock, catchParam, catchBlock, finallyBlock, line };
}

export function createClassDeclaration(
  name: Token,
  methods: FunctionDeclaration[],
  superclass: IdentifierExpr | null = null
): ClassDeclaration {
  return { type: 'ClassDeclaration', name, methods, superclass, line: name.line };
}

export function createImportDeclaration(specifiers: ImportSpecifier[], source: Token): ImportDeclaration {
  return { type: 'ImportDeclaration', specifiers, source, line: source.line };
}

export function createExportDeclaration(
  declaration: Statement | null,
  specifiers: ExportSpecifier[],
  line: number
): ExportDeclaration {
  return { type: 'ExportDeclaration', declaration, specifiers, line };
}

export function createBreakStatement(keyword: Token): BreakStatement {
  return { type: 'BreakStatement', keyword, line: keyword.line };
}

export function createContinueStatement(keyword: Token): ContinueStatement {
  return { type: 'ContinueStatement', keyword, line: keyword.line };
}

export function createThrowStatement(keyword: Token, argument: Expression): ThrowStatement {
  return { type: 'ThrowStatement', keyword, argument, line: keyword.line };
}

export function createProgram(statements: Statement[]): Program {
  return { type: 'Program', statements, line: 1 };
}
